import { type SupabaseClient, createClient } from '@supabase/supabase-js'

// FreightRatesMCP talks to TWO Supabase projects:
//
//   dataClient → the SHARED project (kcibhpjcnqusehqgfxpu, TrackingMCP-owned). We only READ the freight
//     DATABASE here (freight_rate_* views + freight_lane_reliability) and INSERT RFQ leads (freight_rfq).
//     No auth — persistSession:false so it never writes an auth token to storage.
//
//   authClient → the DEDICATED navo24 identity project. Home of the single navo24 login + this product's
//     entitlement (frm_profiles), rate alerts (frm_rate_alerts), and billing edge functions. Standard
//     implicit flow: persistSession + autoRefreshToken + detectSessionInUrl so the magic-link
//     ConfirmationURL lands the user back HERE (freightratesmcp.com/account) already signed in.
//
// The two clients use different project URLs, so supabase-js derives distinct storage keys
// (sb-<ref>-auth-token) and never collide.

const DATA_URL = import.meta.env.VITE_SUPABASE_URL as string
const DATA_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const AUTH_URL = import.meta.env.VITE_NAVO24_SUPABASE_URL as string
const AUTH_ANON = import.meta.env.VITE_NAVO24_SUPABASE_ANON_KEY as string

export const hasSupabase = Boolean(DATA_URL && DATA_ANON)
if (!hasSupabase) {
  console.warn('[supabase] VITE_SUPABASE_URL/ANON_KEY not set: live freight data disabled')
}
export const hasAuth = Boolean(AUTH_URL && AUTH_ANON)
if (!hasAuth) {
  console.warn(
    '[supabase] VITE_NAVO24_SUPABASE_URL/ANON_KEY not set: Pro sign-in disabled (public site unaffected)'
  )
}

// createClient throws on empty url; fall back to a valid placeholder so the SPA still mounts either way.
const PLACEHOLDER_URL = 'https://placeholder.supabase.co'
const PLACEHOLDER_ANON = 'public-anon-placeholder'

/** Shared project — freight views + RFQ. No session. */
export const dataClient = createClient(DATA_URL || PLACEHOLDER_URL, DATA_ANON || PLACEHOLDER_ANON, {
  auth: { persistSession: false },
})

/** navo24 identity project — auth, frm_profiles, frm_rate_alerts, billing. Persistent session. */
export const authClient = createClient(AUTH_URL || PLACEHOLDER_URL, AUTH_ANON || PLACEHOLDER_ANON, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
})

// ── Shared-project sign-in (feature flag, default OFF) ──────────────────────────────────────────────────
// One Navo24 account everywhere: with VITE_SHARED_SIGN_IN=1 the standalone site signs people in on the SHARED
// project (the one the hub uses), so a session from the hub, a magic link or OAuth is the same person in the same
// workspace. authClient is NOT changed (same URL, same default storage key), so an existing identity-project
// session keeps being read and nobody is signed out by the release; the shared session is simply preferred when
// both exist (see active-session.ts). With the flag off, sharedAuthClient is null and nothing below runs.
export const sharedSignInEnabled = /^(1|true|on)$/i.test(
  String(import.meta.env.VITE_SHARED_SIGN_IN ?? '')
)
// Same project URL for both (staging points both at one project): one client, never two GoTrueClients on one
// storage key.
const sameProject = Boolean(DATA_URL) && DATA_URL === AUTH_URL

/** The shared project with a persisted session. Null while the flag is off or the shared project is unset. */
export const sharedAuthClient: SupabaseClient | null =
  sharedSignInEnabled && hasSupabase
    ? sameProject
      ? authClient
      : createClient(DATA_URL, DATA_ANON, {
          auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
        })
    : null

// Back-compat default = the DATA client (most callers read freight data). Auth/alerts/billing import
// authClient explicitly.
export const supabase = dataClient
